// src/components/practices/WayOutOfNegativity.js
// Way Out of Negativity — when something is weighing on you, gratitude can help you look at it
// from another direction. Three fixed questions (what's weighing on you, find the light, turn
// it into gratitude); only the third is a gratitude statement, so the thank-you-tap gate only
// appears there — same shape as Magnificent Outcomes' two-question flow, one step longer.
// Reuses the same shared primitives as every other Practice; no changes to the Practices
// library/nav architecture.
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeStepRing from './shared/PracticeStepRing'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'

const REREAD_MS = 2000
const TOTAL_STEPS = 3

export default function WayOutOfNegativity({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  // Starts on 'step1' directly — the detail screen (AugustPracticeDetail) already shows this
  // Practice's tagline + description before "Begin Practice" is tapped; a separate in-flow intro
  // phase repeating that same copy under a redundant "Begin Practice" title was an extra tap
  // with no new information, not a real step (never counted toward TOTAL_STEPS).
  const [phase, setPhase] = useState('step1') // 'step1' | 'step2' | 'step3' | 'complete'
  const [weighing, setWeighing] = useState('')
  const [light, setLight] = useState('')
  const [gratitude, setGratitude] = useState('')
  const [thanks, setThanks] = useState(0)
  const [reread, setReread] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const weighingTyped = weighing.trim().length > 2
  const lightTyped = light.trim().length > 2
  const gratitudeTyped = gratitude.trim().length > 2
  const isReady = gratitudeTyped && thanks >= 3
  const eyebrow = `Day ${practice.unlockDay} · ${practice.name}`

  const tapThanks = () => {
    if (thanks >= 3) return
    const next = thanks + 1
    setThanks(next)
    if (next < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const handleFinish = async () => {
    if (!isReady || saving) return
    setSaving(true)
    if (session) {
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { weighing, light, gratitude },
      })
    }
    setSaving(false)
    setPhase('complete')
  }

  if (phase === 'step1') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="What's Weighing on You"
          descriptor={`Step 1 of ${TOTAL_STEPS}`}
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={1} total={TOTAL_STEPS} />

          <p className="practice-question">What's weighing on you?</p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="Something on your mind…"
              value={weighing}
              onChange={e => setWeighing(e.target.value)}
            />
          </div>

          <PrimaryButton onClick={weighingTyped ? () => setPhase('step2') : undefined} disabled={!weighingTyped}>
            <span>Next</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  if (phase === 'step2') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="Find the Light"
          descriptor={`Step 2 of ${TOTAL_STEPS}`}
          showBack onBack={() => setPhase('step1')}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={2} total={TOTAL_STEPS} />

          <p className="practice-question">What can you appreciate, learn from, or see differently about it?</p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="Even something small…"
              value={light}
              onChange={e => setLight(e.target.value)}
            />
          </div>

          <PrimaryButton onClick={lightTyped ? () => setPhase('step3') : undefined} disabled={!lightTyped}>
            <span>Next</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  if (phase === 'step3') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="Turn It Into Gratitude"
          descriptor={`Step 3 of ${TOTAL_STEPS}`}
          showBack onBack={() => setPhase('step2')}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={3} total={TOTAL_STEPS} />

          <p className="practice-question">What can you be grateful for now?</p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="I'm grateful for…"
              value={gratitude}
              onChange={e => setGratitude(e.target.value)}
            />
          </div>

          <PracticeThankYouTap
            count={thanks}
            reread={reread}
            canTap={gratitudeTyped}
            onTap={tapThanks}
            writtenText={gratitude}
          />

          <PrimaryButton onClick={isReady ? handleFinish : undefined} disabled={!isReady || saving}>
            <span>Find the Way Out</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // phase === 'complete'
  return (
    <PracticeComplete
      headline="Practice Complete"
      subline="You found another way to see it."
      onDone={onComplete}
    />
  )
}
